import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppFooter } from '../components/AppFooter';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/LoginPage.css';
import logo from '../assets/logo.png';

interface PasswordStrength {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get user info from location state (passed from login)
  const user = location.state?.user;

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // If no user info, redirect to login
  if (!user) {
    navigate('/login');
    return null;
  }

  // Calculate password strength
  const getPasswordStrength = (password: string): PasswordStrength => {
    return {
      minLength: password.length >= 10,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?/`~]/.test(password)
    };
  };

  const strength = getPasswordStrength(formData.newPassword);
  const isStrongPassword = Object.values(strength).every(Boolean);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (!isStrongPassword) {
      setError('Password does not meet strength requirements');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            staffID: user.staffID,
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to change password');
      }

      // Auto-login after password change - store token and user directly
      if (result.data.token && result.data.user) {
        localStorage.setItem('tacolcy_token', result.data.token);
        localStorage.setItem('tacolcy_user', JSON.stringify(result.data.user));
        window.location.href = '/dashboard'; // Force full page reload to update auth state
      } else {
        throw new Error('No token received');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
          <h2 className="login-title">Change Your Password</h2>
          <p className="login-subtitle">Welcome, {user.firstName}! You must change your temporary password before continuing.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}
          {/* Current Password */}
          <div className="form-group">
            <label htmlFor="currentPassword">Current Temporary Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showCurrentPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showNewPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Password Strength Indicator */}
          {formData.newPassword && (
            <div className="password-strength" style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Password Requirements:</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ color: strength.minLength ? 'green' : 'red' }}>
                  {strength.minLength ? '✓' : '✗'} At least 10 characters
                </li>
                <li style={{ color: strength.hasUppercase ? 'green' : 'red' }}>
                  {strength.hasUppercase ? '✓' : '✗'} Contains uppercase letter (A-Z)
                </li>
                <li style={{ color: strength.hasLowercase ? 'green' : 'red' }}>
                  {strength.hasLowercase ? '✓' : '✗'} Contains lowercase letter (a-z)
                </li>
                <li style={{ color: strength.hasNumber ? 'green' : 'red' }}>
                  {strength.hasNumber ? '✓' : '✗'} Contains number (0-9)
                </li>
                <li style={{ color: strength.hasSpecial ? 'green' : 'red' }}>
                  {strength.hasSpecial ? '✓' : '✗'} Contains special character (!@#$%^&*, etc.)
                </li>
              </ul>
            </div>
          )}

          <button
            type="submit"
            className="button-primary login-button"
            disabled={loading || !isStrongPassword}
          >
            {loading ? 'Changing Password...' : 'Change Password & Continue'}
          </button>
        </form>

        <AppFooter />
      </div>
    </div>
  );
}
